import * as React from 'react'
import { useQuery } from 'react-query'
import { Icon } from 'antd-mobile'
import { RouteComponentProps, useHistory } from 'react-router-dom'
import { getFaqList } from '@feedback/services'
import { getAppConfig } from '@feedback/utils/CONST'
import { getVestCategoryIcon } from '@feedback/utils/categoryIcons'
import {
  getTextByKey,
  reportFn,
  tracking
} from '@feedback/utils/util'
import VestLayout from '../Layout'
import './index.scss'

export interface VestCategoryItem {
  /** 类别展示名（与后台 faq_type 一致） */
  faq_type: string
  /** 若后台后续带 id，透传给列表屏；当前接口只有 faq_type */
  type_id?: string | number
}

/**
 * 拉取 FAQ 列表并抽出类别维度（faq_type）。
 * 数据源与既有首页一致：getFaqList；马甲站只消费分类名做网格导航。
 */
export async function fetchVestCategories(): Promise<VestCategoryItem[]> {
  const { appid } = getAppConfig() || {}
  const { code, data, message } = (await getFaqList({ page: 1, limit: 50 })) || {}

  if (code !== 0) {
    return Promise.reject(new Error(message || 'Failed to load FAQ'))
  }

  // 埋点：沿用现有首页列表曝光点位
  if (appid === 'cm1006') {
    tracking({
      _step_id: '',
      _feedback_page_id: 1,
      _postion_id: 5,
      _search_words: '',
      _faq_id: '',
      _src_faq_id: ''
    })
  } else {
    reportFn({ parm1: 1, parm2: 5, parm6: '' })
  }

  const list: any[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.list)
    ? data.list
    : []

  // 按 faq_type 去重，保序
  const seen = new Set<string>()
  const categories: VestCategoryItem[] = []
  list.forEach((item) => {
    const name = (item?.faq_type || item?.name || '').trim()
    if (!name || seen.has(name)) return
    seen.add(name)
    categories.push({
      faq_type: name,
      type_id: item?.type_id ?? item?._id
    })
  })
  return categories
}

interface IProps extends RouteComponentProps {
  [key: string]: any
}

/**
 * 马甲站首页：Hello 标语 + 类别 2 列网格。
 * 壳层（brand bar / 底栏）由 VestLayout 提供；非马甲站不走此组件。
 */
const VestHome: React.FC<IProps> = () => {
  const history = useHistory()
  const { data, error, isError, isLoading } = useQuery<
    VestCategoryItem[],
    Error
  >('vestHomeCategories', fetchVestCategories, {
    staleTime: 60 * 1000
  })

  const onCategoryClick = (category: VestCategoryItem) => {
    const params = new URLSearchParams({
      faq_type: category.faq_type
    })
    if (category.type_id != null && category.type_id !== '') {
      params.set('type_id', String(category.type_id))
    }
    // 04 票落地 vest/questionList；路由 path 已预留
    history.push(`/questionList?${params.toString()}`)
  }

  return (
    <VestLayout showBottomBar showSearch bodyClassName='vest-home-body'>
      <div className='vest-home'>
        <h1 className='vest-home__title'>
          <span>{getTextByKey('hello')}</span>
          <span>{getTextByKey('What can we do to help')}</span>
        </h1>

        {isLoading ? (
          <div className='vest-home__status'>
            <Icon type='loading' />
          </div>
        ) : isError ? (
          <div className='vest-home__status'>
            {error?.message || getTextByKey('Nothing more to load')}
          </div>
        ) : !data || data.length < 1 ? (
          <div className='vest-home__status'>
            {getTextByKey('Nothing more to load')}
          </div>
        ) : (
          <div className='vest-home__grid' role='list'>
            {data.map((item) => (
              <button
                key={item.faq_type}
                type='button'
                className='vest-home__card'
                role='listitem'
                onClick={() => onCategoryClick(item)}
              >
                <img
                  className='vest-home__icon'
                  src={getVestCategoryIcon(item.faq_type)}
                  alt=''
                  aria-hidden='true'
                />
                <span className='vest-home__label'>{item.faq_type}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </VestLayout>
  )
}

export default VestHome
